import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import tinycolor from 'tinycolor2';

import { BaseComponent } from '../../../core/abstractions/base.component';
import { ActionButtonModel } from '../../../models/core/action-button.model';
import { speedDialFabAnimations } from './speed-dial-fab.animations';

export const speedDialButtons = signal<ActionButtonModel[]>([]);

@Component({
    selector: 'sl-speed-dial-fab',
    templateUrl: './speed-dial-fab.component.html',
    styleUrls: ['./speed-dial-fab.component.scss'],
    animations: speedDialFabAnimations,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SpeedDialFabComponent extends BaseComponent {
    readonly isExpanded = signal(false);
    readonly localButtons = speedDialButtons;
    readonly buttons = computed(() =>
        this.localButtons().map(
            b =>
                new ActionButtonModel({
                    ...b,
                    iconColor: this.contrastColorCache.get(b.color)
                })
        )
    );

    private readonly contrastColorCache = new Map<string, string>([
        ['', 'black'],
        ['primary', this.getIconColor('current-primary-color')],
        ['accent', this.getIconColor('current-accent-color')],
        ['warn', this.getIconColor('current-warn-color')]
    ]);

    private readonly router = inject(Router);

    constructor() {
        super();
        this.router.events.subscribe(val => {
            if (val instanceof NavigationEnd) {
                // clear the buttons when successfully navigating away
                this.localButtons.set([]);
            }
        });
    }

    onToggleFab(): void {
        this.isExpanded.set(!this.isExpanded());
    }

    onButtonClick(button: ActionButtonModel, event: MouseEvent): void {
        this.isExpanded.set(false);

        if (!!button.clickFunction) {
            button.clickFunction(event);
        }
    }

    isTranslationKey(value: string): boolean {
        if (!value) {
            return false;
        }

        const res = value.trim().match(/.+\.\S+/g);

        return !!res && res.length > 0;
    }

    private getIconColor(selector: string): string {
        let cssColor = '';

        for (let i = 0, l = document.styleSheets.length; i < l; i++) {
            const sheet = document.styleSheets[i];

            if (!sheet.cssRules) {
                continue;
            }

            for (let j = 0, k = sheet.cssRules.length; j < k; j++) {
                const rule = sheet.cssRules[j] as any;

                if (rule.selectorText && rule.selectorText.split(',').indexOf(selector) !== -1) {
                    cssColor = rule.style['color'];
                    break;
                }
            }
        }

        const color = tinycolor(cssColor);

        return color.isLight() ? 'black' : 'white';
    }
}
